import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import "../styles/PhoneInput.css";
import GoogleButton from "../components/GoogleButton";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuthStore } from "../store/authStore";

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    signInWithPhone,
    verifyPhoneOtp,
    isLoading,
    isOtpSent,
    isAuthenticated,
    user,
  } = useAuthStore();

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [otp, setOtp] = useState("");
  const resetLocalError = () => setError(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const handleSignInError = (err: Error) => {
    const errorMessage = err?.message?.startsWith("auth.errors.")
      ? t(err.message)
      : err?.message || t("loginPage.googleSignInError");
    setError(errorMessage);
    useAuthStore.setState({
      isOtpSent: false,
      phoneNumberForVerification: null,
    });
    setOtp("");
  };

  async function handleSendOtp() {
    resetLocalError();
    try {
      if (!phoneNumber) {
        throw new Error("auth.errors.invalidPhoneNumber");
      }
      await signInWithPhone(phoneNumber);
    } catch (err) {
      setError(err instanceof Error ? err.message : "auth.errors.unknown");
    }
  }

  async function handleVerifyOtp() {
    resetLocalError();
    try {
      await verifyPhoneOtp(otp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "auth.errors.invalidOtp");
    }
  }

  function resetPhoneLogin() {
    resetLocalError();
    setPhoneNumber("");
    setOtp("");
    useAuthStore.setState({
      isOtpSent: false,
      phoneNumberForVerification: null,
    });
  }

  const redirectMessageKey = location.state?.messageKey;

  // Derive default country code from locale
  const currentLang = i18n.language; // e.g., "en-US", "fr-FR", "es"
  let defaultCountryCode: CountryCode | undefined = undefined;
  if (currentLang && currentLang.includes("-")) {
    const parts = currentLang.split("-");
    defaultCountryCode = parts[parts.length - 1].toUpperCase() as CountryCode;
  } else {
    defaultCountryCode = currentLang.toUpperCase() as CountryCode;
  }

  useEffect(() => {
    if (isAuthenticated && !user?.is_anonymous) {
      navigate("/home", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isOtpSent) {
      otpInputRef.current?.focus();
    }
  }, [isOtpSent]);

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      <Header />

      {redirectMessageKey === "sign_in_for_credits" && (
        <div className="w-full max-w-md mx-auto mt-24 p-4 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white rounded-lg shadow-md text-center animate-gradient-x">
          <p className="font-semibold">
            {t(
              "loginPage.signInForCredits",
              "Unlock 3 free credits! Sign in to continue creating amazing things."
            )}
          </p>
        </div>
      )}

      <main className="flex-grow flex items-center justify-center">
        <div className="w-full max-w-md bg-gray-900/50 p-6 md:p-8 rounded-lg border border-gray-800 shadow-xl">
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-500/50 text-red-300 rounded-md text-sm">
              {error.startsWith("auth.errors.") ? t(error) : error}
            </div>
          )}

          {/* Conditionally render Google Button and OR separator */}
          {!isOtpSent && (
            <>
              <div className="w-full mb-6">
                <GoogleButton
                  textKey="googleButton.signIn"
                  onError={handleSignInError}
                />
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900/50 text-gray-400">
                    {t("loginPage.or", "OR")}
                  </span>
                </div>
              </div>
            </>
          )}

          <div>
            <div className="flex flex-col space-y-4">
              {!isOtpSent ? (
                <>
                  <div>
                    <label
                      htmlFor="phone-input"
                      className="block text-sm font-medium text-gray-400 mb-1"
                    >
                      {t("loginPage.phonePlaceholder", "Enter phone number")}
                    </label>
                    <PhoneInput
                      id="phone-input"
                      value={phoneNumber}
                      onChange={(value) => {
                        setPhoneNumber(value);
                        resetLocalError();
                      }}
                      defaultCountry={defaultCountryCode}
                      international
                      withCountryCallingCode
                      className="input input-bordered w-full bg-gray-800 text-white placeholder-gray-500 rounded-md py-3 px-4"
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    className={`btn w-full rounded-md py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium transition-all duration-200 ${
                      isLoading ||
                      !(phoneNumber && isPossiblePhoneNumber(phoneNumber))
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleSendOtp}
                    disabled={
                      isLoading ||
                      !(phoneNumber && isPossiblePhoneNumber(phoneNumber))
                    }
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("common.loading", "Loading...")}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        {t("loginPage.signInWithPhone", "Sign in with Phone")}
                      </span>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor="otp-input"
                      className="block text-sm font-medium text-gray-400 mb-1"
                    >
                      {t(
                        "loginPage.verificationCode",
                        "Enter Verification Code Below"
                      )}
                    </label>
                    <input
                      id="otp-input"
                      ref={otpInputRef}
                      type="text"
                      inputMode="numeric"
                      placeholder={t("loginPage.otpPlaceholder", "Enter OTP")}
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        resetLocalError();
                      }}
                      className="input input-bordered w-full bg-gray-800 text-white placeholder-gray-500 rounded-md py-3 px-4"
                      disabled={isLoading}
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <button
                    className={`btn w-full rounded-md py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium transition-all duration-200 ${
                      isLoading || !otp || otp.length !== 6
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleVerifyOtp}
                    disabled={isLoading || !otp || otp.length !== 6}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("common.loading", "Loading...")}
                      </span>
                    ) : (
                      t("loginPage.verifyOtpButton", "Verify Passcode")
                    )}
                  </button>
                  <button
                    className="btn w-full rounded-md py-2 border border-gray-700 bg-transparent hover:bg-gray-800 text-gray-300 hover:text-white text-sm font-medium transition-all duration-200"
                    onClick={resetPhoneLogin}
                    disabled={isLoading}
                  >
                    {t("loginPage.cancelPhoneLogin", "Use different number")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LoginPage;
